// intakeDraftStore — localStorage-backed draft for the Smart Intake
// wizard (plan §P2.16 — UI Workflow Consistency Audit).
//
// Background. The audit (§1.4) flagged that the entire Smart Intake
// form lives in component-local ``useState`` — a reload mid-intake
// silently loses everything the user typed, even though projects are
// first-class persistent objects. The fix is a tiny zustand slice
// persisted to localStorage so reload / accidental tab close / bounce
// to the Hub all preserve the work in progress.
//
// Scope. We persist *form fields only*. Pending file uploads
// (``contentFiles`` / ``formatFiles``) cannot be JSON-serialised, so
// they are persisted separately in IndexedDB via
// lib/pendingFileStore.ts (slot 'intake') and restored on resume —
// keeping queued documents from being silently dropped on reload.
//
// The store exposes a single keyed slot ("active draft"). When the
// user successfully submits the wizard, ``clearDraft`` resets the slot
// so a future "+ New project" click starts fresh.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type {
  AudienceTag,
  ShapeId,
} from '@/components/intake/shapes'
import type {
  InterventionBudget,
  RegulatoryFlag,
} from '@/types'

export interface IntakeDraft {
  name: string
  problem_statement: string
  shape: ShapeId | null
  vertical: string | null
  regulatory: RegulatoryFlag[]
  regulatoryUserEdited: boolean
  audience: AudienceTag[]
  cadence: InterventionBudget
  /**
   * Document-control owner + approver pair. Required by the backend's
   * strict-mode intake validator whenever ANY non-`'none'` regulatory
   * flag is set. Optional in the draft store so legacy drafts (pre-
   * 2026-05-28) still hydrate; SmartIntakePage falls back to empty
   * strings when these are missing on resume.
   */
  document_owner?: string
  document_approver?: string
  /**
   * AI-governance ratification. Persisted so a user who picked
   * "Not applicable" doesn't have to re-pick on resume. Optional in
   * the draft store so pre-2026-05-28 drafts still hydrate.
   */
  ai_governance_disposition?: 'in_scope' | 'not_applicable' | null
  ai_governance_ratified_by?: string
  ai_governance_rationale?: string
  /**
   * Deployment-architecture fields the Operations Architect agent
   * needs to fill the manifest without TBDs (PEG-DEPLOYMENT-
   * MANIFEST-001). All optional — leaving any blank is a valid
   * "defer until vendor selection" choice that the architect can
   * ratify or waive at Phase 5. Optional on the draft so pre-
   * 2026-05-28 drafts still hydrate.
   */
  cloud_provider?: string
  primary_region?: string
  container_runtime?: string
  orchestrator?: string
  iac_framework?: string
  secrets_store?: string
  secrets_rotation_policy?: string
  oncall_primary_rotation?: string
  oncall_runbook_index?: string
  oncall_pager_severity_thresholds?: string
  /** Wizard step (1/2/3/4) the user was on when they bounced. */
  step: 1 | 2 | 3 | 4
  /** ISO timestamp so the resume banner can say "saved 8 min ago". */
  savedAt: string | null
}

interface IntakeDraftStore {
  draft: IntakeDraft | null
  saveDraft: (draft: Omit<IntakeDraft, 'savedAt'>) => void
  clearDraft: () => void
  hasDraft: () => boolean
}

export const useIntakeDraftStore = create<IntakeDraftStore>()(
  persist(
    (set, get) => ({
      draft: null,
      saveDraft: (draft) =>
        set({
          draft: { ...draft, savedAt: new Date().toISOString() },
        }),
      clearDraft: () => set({ draft: null }),
      hasDraft: () => {
        const d = get().draft
        if (!d) return false
        // Treat the draft as empty when the user hasn't typed anything
        // meaningful — avoids surfacing a "resume?" banner for the
        // tiniest accidental click into the wizard.
        return d.name.trim().length > 0 || d.problem_statement.trim().length > 0
      },
    }),
    { name: 'ai_alm_intake_draft_v07' },
  ),
)

/**
 * Friendly relative-time string for the resume banner. Returns "just
 * now" / "5 min ago" / "2 h ago" / falls back to a date when older
 * than 24 h.
 */
export function formatRelativeAge(iso: string | null | undefined): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000))
  if (seconds < 30) return 'just now'
  if (seconds < 60) return `${seconds} s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return new Date(iso).toLocaleString()
}
